import { Contract } from '@ethersproject/contracts'
import { getAddress } from '@ethersproject/address'
import { AddressZero } from '@ethersproject/constants'
import { JsonRpcSigner, Web3Provider } from '@ethersproject/providers'
import { BigNumber, BigNumberish, formatFixed, parseFixed } from '@ethersproject/bignumber'
import { ChainId } from './token'

const names = ['wei', 'kwei', 'mwei', 'gwei', 'szabo', 'finney', 'ether']

export const currentTimestamp = () => new Date().getTime()

export function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined
}

// returns the checksummed address if the address is valid, otherwise returns false
export function isAddress(value: any): string | false {
  try {
    return getAddress(value)
  } catch {
    return false
  }
}

const BSCSCAN_PREFIXES: { [chainId in ChainId]: string } = {
  56: 'bscscan.com',
  97: 'testnet.bscscan.com',
  9000: 'btsscan.com',
}

export function getBscScanLink(chainId: ChainId, data: string, type: 'transaction' | 'token' | 'address'): string {
  const prefix = `https://${BSCSCAN_PREFIXES[chainId] || BSCSCAN_PREFIXES[ChainId.MAINNET]}`

  switch (type) {
    case 'transaction': {
      return `${prefix}/tx/${data}`
    }
    case 'token': {
      return `${prefix}/token/${data}`
    }
    case 'address':
    default: {
      return `${prefix}/address/${data}`
    }
  }
}

// shorten the checksummed version of the input address to have 0x + 4 characters at start and end
export function shortenAddress(address: string, chars = 4): string {
  const parsed = isAddress(address)
  if (!parsed) {
    throw Error(`Invalid 'address' parameter '${address}'.`)
  }
  return `${parsed.substring(0, chars + 2)}...${parsed.substring(42 - chars)}`
}

// add 10%
export function calculateGasMargin(value: BigNumber): BigNumber {
  return value.mul(BigNumber.from(10000).add(BigNumber.from(1000))).div(BigNumber.from(10000))
}

// account is not optional
export function getSigner(library: Web3Provider, account: string): JsonRpcSigner {
  return library.getSigner(account).connectUnchecked()
}

// account is optional
export function getProviderOrSigner(library: Web3Provider, account?: string): Web3Provider | JsonRpcSigner {
  return account ? getSigner(library, account) : library
}

// account is optional
export function getContract(address: string, ABI: any, library: Web3Provider, account?: string): Contract {
  if (!isAddress(address) || address === AddressZero) {
    throw Error(`Invalid 'address' parameter '${address}'.`)
  }

  return new Contract(address, ABI, getProviderOrSigner(library, account) as any)
}

export function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}

export function toLocaleString(x) {
  return x.toLocaleString('fullwide', { useGrouping: false })
}

export function formatUnits(value: BigNumberish, unitName?: string | BigNumberish): string {
  if (typeof unitName === 'string') {
    const index = names.indexOf(unitName)
    if (index !== -1) {
      unitName = 3 * index
    }
  }
  return formatFixed(value, unitName != null ? unitName : 18)
}

export function parseUnits(value: string, unitName?: BigNumberish): BigNumber {
  if (typeof value !== 'string') {
    console.log('value must be a string', 'value', value)
  }
  if (typeof unitName === 'string') {
    const index = names.indexOf(unitName)
    if (index !== -1) {
      unitName = 3 * index
    }
  }
  return parseFixed(value, unitName != null ? unitName : 18)
}

export function toCapitalize(word: string) {
  return word.charAt(0).toUpperCase() + word.toLowerCase().slice(1)
}

export const abbreviateHex = (address, abbrLength = 0) => {
  return address
    ? address.slice(0, 12 - abbrLength).padEnd(15 - abbrLength, '.') + address.slice(address.length - 3, address.length)
    : ''
}

export function addCommasToNumber(number: number | string, decimalPlaces = 2): string {
  try {
    if (Number.isNaN(+number)) {
      throw new Error('Not a valid number')
    }

    // Nhân số với 10^decimalPlaces, sau đó làm tròn xuống bằng Math.floor
    const multiplier = 10 ** decimalPlaces
    const roundedNumber = Math.floor(+number * multiplier) / multiplier

    const numberWithCommas: string = roundedNumber.toFixed(decimalPlaces).replace(/\B(?=(\d{3})+(?!\d))/g, ',')

    return numberWithCommas
  } catch {
    return '0'
  }
}
